"use client"

import { AlertTriangle, CheckCircle, Clock, MapPin, Camera } from "lucide-react"
import { formatDistanceToNow } from "date-fns"

const AlertsTable = ({ alerts, onAcknowledge, compact = false }) => {
  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "critical":
        return <AlertTriangle className="h-5 w-5 text-red-500" />
      case "warning":
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />
      default:
        return <AlertTriangle className="h-5 w-5 text-blue-500" />
    }
  }

  const getSeverityClass = (severity) => {
    switch (severity) {
      case "critical":
        return "alert-critical"
      case "warning":
        return "alert-warning"
      default:
        return "alert-info"
    }
  }

  if (compact) {
    return (
      <div className="divide-y divide-gray-200">
        {alerts.map((alert) => (
          <div key={alert.id} className="p-4 hover:bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {getSeverityIcon(alert.severity)}
                <div>
                  <div className="text-sm font-medium text-gray-900">{alert.message}</div>
                  <div className="flex items-center space-x-3 text-xs text-gray-500 mt-1">
                    <span className="flex items-center">
                      <MapPin className="h-3 w-3 mr-1" />
                      {alert.deviceId}
                    </span>
                    <span className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                {alert.acknowledged ? (
                  <span className="badge badge-success">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Resolved
                  </span>
                ) : (
                  <button onClick={() => onAcknowledge(alert.id)} className="btn btn-primary btn-sm">
                    Acknowledge
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="table-container">
      <table className="table">
        <thead className="table-header">
          <tr>
            <th className="table-header-cell">Alert</th>
            <th className="table-header-cell">Device</th>
            <th className="table-header-cell">Location</th>
            <th className="table-header-cell">Time</th>
            <th className="table-header-cell">Status</th>
            <th className="table-header-cell">Actions</th>
          </tr>
        </thead>
        <tbody className="table-body">
          {alerts.map((alert) => (
            <tr key={alert.id} className="table-row">
              <td className="table-cell">
                <div className="flex items-center">
                  {getSeverityIcon(alert.severity)}
                  <div className="ml-3">
                    <div
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getSeverityClass(alert.severity)}`}
                    >
                      {alert.severity.toUpperCase()}
                    </div>
                    <div className="text-sm font-medium text-gray-900 mt-1">
                      {alert.message}
                      {alert.type === "vibration" && alert.subType && (
                        <span
                          className={`ml-2 inline-flex px-2 py-1 text-xs font-medium rounded ${
                            alert.subType === "bomb" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {alert.subType.toUpperCase()}
                        </span>
                      )}
                      {alert.cameraCapture && (
                        <span className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                          <Camera className="h-3 w-3 mr-1" />
                          Image Available
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </td>
              <td className="table-cell">{alert.deviceId}</td>
              <td className="table-cell">
                <div className="flex items-center text-sm text-gray-700">
                  <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                  {alert.location.address}
                </div>
              </td>
              <td className="table-cell">
                <div className="flex items-center text-sm text-gray-700">
                  <Clock className="h-4 w-4 mr-1 text-gray-400" />
                  {formatDistanceToNow(new Date(alert.timestamp), { addSuffix: true })}
                </div>
              </td>
              <td className="table-cell">
                {alert.acknowledged ? (
                  <span className="badge badge-success">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Acknowledged
                  </span>
                ) : (
                  <span className="badge badge-danger">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Pending
                  </span>
                )}
              </td>
              <td className="table-cell">
                {!alert.acknowledged && (
                  <button onClick={() => onAcknowledge(alert.id)} className="btn btn-primary btn-sm">
                    Acknowledge
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default AlertsTable
